'use client'

import { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'

const WARNING_STYLE_THRESHOLD_MINUTES = 5
const PROLONG_MODAL_THRESHOLD_SECONDS = 60

interface UseSessionCountdownParams {
  expireAt: string // UTC ISO date string
  /** Pause the 1s tick when false (e.g. a modal that only needs it while open). Default true. */
  enabled?: boolean
  onExpiringSoon?: () => void
}

export interface SessionCountdown {
  /** True once the session has expired. */
  isExpired: boolean
  /** True when under the warning threshold (5 min) or expired. */
  isExpiringSoon: boolean
  /** Seconds within the current minute (0 once expired). */
  seconds: number
  /** Whole minutes remaining (0 once expired). */
  totalMinutes: number
}

/**
 * Theme-agnostic session countdown. Owns the timer (1s tick), luxon diff and
 * expiry thresholds; fires `onExpiringSoon` once when crossing the 60s mark.
 * Returns raw values — each theme's banner formats/renders its own UI.
 */
export const useSessionCountdown = ({
  expireAt,
  onExpiringSoon,
  enabled = true,
}: UseSessionCountdownParams): SessionCountdown => {
  const [totalMinutes, setTotalMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const [isExpiringSoon, setIsExpiringSoon] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const expiringSoonFired = useRef(false)

  // Reset the fired flag when expireAt changes (after prolong)
  useEffect(() => {
    expiringSoonFired.current = false
    setIsExpired(false)
    setIsExpiringSoon(false)
  }, [expireAt])

  useEffect(() => {
    if (!enabled || !expireAt) return

    const update = () => {
      const now = DateTime.now()
      const expiry = DateTime.fromISO(expireAt, { zone: 'utc' }).toLocal()
      const diff = expiry.diff(now, ['minutes', 'seconds'])
      const totalSeconds = diff.as('seconds')

      if (totalSeconds <= 0) {
        setTotalMinutes(0)
        setSeconds(0)
        setIsExpiringSoon(true)
        setIsExpired(true)
        return
      }

      const minutes = Math.floor(diff.minutes)
      const secs = Math.floor(diff.seconds % 60)

      setTotalMinutes(minutes)
      setSeconds(secs)
      setIsExpiringSoon(minutes < WARNING_STYLE_THRESHOLD_MINUTES)

      if (totalSeconds <= PROLONG_MODAL_THRESHOLD_SECONDS && !expiringSoonFired.current) {
        expiringSoonFired.current = true
        onExpiringSoon?.()
      }
    }

    update()

    const interval = setInterval(update, 1000)

    return () => clearInterval(interval)
  }, [expireAt, onExpiringSoon, enabled])

  return { totalMinutes, seconds, isExpiringSoon, isExpired }
}
